import React, { useState, useEffect } from "react";
import { Text, Pill } from "../../components";
import { Toolbar, IconButton, Tooltip } from "@material-ui/core";
import FormikInput from "../../components/formik/FormikInput";
import FormikJsonInput from "../../components/formik/FormikJsonInput";
import { makeStyles } from "@material-ui/styles";
import _ from "lodash";
import { Form, setNestedObjectValues, withFormik } from "formik";
import { useWorkflowDef } from "../../data/workflow";
import FormikVersionDropdown from "../../components/formik/FormikVersionDropdown";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PlaylistAddIcon from "@material-ui/icons/PlaylistAdd";
import SaveIcon from "@material-ui/icons/Save";
import { colors } from "../../theme/variables";
import { timestampRenderer } from "../../utils/helpers";
import * as Yup from "yup";
import FormikWorkflowNameInput from "../../components/formik/FormikWorkflowNameInput";
import { useRouteMatch } from "react-router-dom";
import { useWorkflowVersions } from "../../data/workflow";

const useStyles = makeStyles((theme) => ({
  name: {
    width: "50%",
  },
  submitButton: {
    float: "right",
  },
  toolbar: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: "12px 16px",
    minHeight: "64px",
    height: "64px",
    boxSizing: "border-box",
  },
  workflowName: {
    fontWeight: 600,
    fontSize: "18px",
    color: "#1a1a1a",
    flex: 1,
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    backgroundColor: "#f8f9fa",
  },
  fields: {
    width: "100%",
    padding: "0px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    maxWidth: "900px",
    margin: "0 auto",
  },
  fieldGroup: {
    backgroundColor: "#ffffff",
    // borderRadius: "16px",
    padding: "32px",

    transition: "all 0.2s ease",
  },
  fieldSpacing: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    "& > *": {
      marginBottom: "0 !important",
    },
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  statusArea: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    marginLeft: "auto",
  },
  createdText: {
    fontSize: "13px",
    color: "#666666",
    fontStyle: "italic",
    fontWeight: 400,
  },
  modifiedPill: {
    "& .MuiChip-root": {
      backgroundColor: "#fff3cd",
      color: "#856404",
      border: "1px solid #ffeaa7",
      fontSize: "12px",
      fontWeight: 600,
      height: "28px",
      borderRadius: "14px",
      boxShadow: "0 2px 4px rgba(133, 100, 4, 0.1)",
      "& .MuiChip-label": {
        paddingLeft: "10px",
        paddingRight: "10px",
      },
    },
  },
}));

Yup.addMethod(Yup.string, "isJson", function (message) {
  return this.test("is-json", message, function (value) {
    const { path, createError } = this;
    if (_.isEmpty(value)) return true;
    try {
      JSON.parse(value);
      return true;
    } catch (error) {
      return createError({ path, message: message ?? "Invalid JSON" });
    }
  });
});

const validationSchema = Yup.object({
  workflowName: Yup.string().required("Workflow Name is required"),
  workflowInput: Yup.string().isJson("Invalid JSON in workflow input"),
  taskToDomain: Yup.string().isJson("Invalid JSON in task to domain"),
});

export default withFormik({
  enableReinitialize: false,
  mapPropsToValues: ({ selectedRun }) =>
    runPayloadToFormData(_.get(selectedRun, "runPayload")),
  validationSchema: validationSchema,
})(WorkbenchForm);

function WorkbenchForm(props) {
  const {
    values,
    validateForm,
    validateField,
    setTouched,
    setFieldValue,
    dirty,
    selectedRun,
    saveRun,
    executeRun,
    errors,
    touched,
  } = props;
  const classes = useStyles();
  const { workflowName, workflowVersion } = values;
  const createTime = selectedRun ? selectedRun.createTime : undefined;

  const match = useRouteMatch();
  const selectedWorkflowName = _.get(match, "params.name");
  var selectedWorkflowVersion = _.get(match, "params.version");
  var prevWorkflowName = workflowName;
  var prevWorkflowVersion = workflowVersion;

  const [latestVersion, setLatestVersion] = useState(1);
  const { refetch } = useWorkflowDef(workflowName, workflowVersion, null, {
    onSuccess: populateInput,
    enabled: false,
  });

  const { data: namesAndVersions } = useWorkflowVersions(selectedWorkflowName);

  function triggerPopulateInput() {
    refetch();
  }

  useEffect(() => {
    if (
      workflowName !== prevWorkflowName &&
      workflowVersion == prevWorkflowVersion
    ) {
      setFieldValue(workflowVersion, "");
    }
    values.workflowVersion = "";
  }, [workflowName]);

  useEffect(() => {
    let tempLatestVersion = 1;
    if (namesAndVersions && namesAndVersions.length > 0) {
      namesAndVersions.sort(function (a, b) {
        tempLatestVersion = a > b ? a : b;
        setLatestVersion(tempLatestVersion);
      });
    }
    values.workflowVersion = tempLatestVersion;
  }, [namesAndVersions]);

  useEffect(() => {
    if (selectedWorkflowName && selectedWorkflowVersion) {
      values.workflowName = selectedWorkflowName;
      values.workflowVersion = selectedWorkflowVersion;
    }
  }, [selectedWorkflowName, selectedWorkflowVersion]);

  async function populateInput(workflowDef) {
    let bootstrap = {};

    try {
      await validationSchema.validateAt("workflowInput", values);
      if (!_.isEmpty(values.workflowInput)) {
        const existing = JSON.parse(values.workflowInput);
        bootstrap = _.pickBy(existing, (v) => v !== "");
      }

      if (workflowDef.inputParameters) {
        for (let param of workflowDef.inputParameters) {
          if (!_.has(bootstrap, param)) {
            bootstrap[param] = "";
          }
        }

        setFieldValue("workflowInput", JSON.stringify(bootstrap, null, 2));
      }
    } catch (e) {
      validateField("workflowInput");
    }
  }

  function handleRun() {
    if (!values.workflowVersion || values.workflowVersion == "") {
      values.workflowVersion = latestVersion;
    }
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        const payload = formDataToRunPayload(values);
        if (!dirty && createTime) {
          executeRun(createTime, payload);
        } else {
          const newRun = saveRun(payload);
          executeRun(newRun.createTime, payload);
        }
      } else {
        setTouched(setNestedObjectValues(errors, true));
      }
    });
  }

  function handleSave() {
    if (!values.workflowVersion || values.workflowVersion == "") {
      values.workflowVersion = latestVersion;
    }
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        const payload = formDataToRunPayload(values);
        saveRun(payload);
      } else {
        setTouched(setNestedObjectValues(errors, true));
      }
    });
  }

  return (
    <Form className={classes.main}>
      <Toolbar className={classes.toolbar}>
        <Text className={classes.workflowName}>Workflow Workbench</Text>

        <div className={classes.actionButtons}>
          <Tooltip title="Execute Workflow">
            <IconButton
              onClick={handleRun}
              color="primary"
              disabled={!values.workflowName || values.workflowName == ""}
            >
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Save Workflow Trigger">
            <div>
              <IconButton
                disabled={!dirty}
                onClick={handleSave}
                color="primary"
              >
                <SaveIcon />
              </IconButton>
            </div>
          </Tooltip>

          <Tooltip title="Populate Input Parameters">
            <div>
              <IconButton
                disabled={!values.workflowName || values.workflowName == ""}
                onClick={triggerPopulateInput}
                color="primary"
              >
                <PlaylistAddIcon />
              </IconButton>
            </div>
          </Tooltip>
        </div>

        <div className={classes.statusArea}>
          {dirty && (
            <div className={classes.modifiedPill}>
              <Pill label="Modified" />
            </div>
          )}
          {createTime && (
            <Text className={classes.createdText}>
              Created: {timestampRenderer(createTime)}
            </Text>
          )}
        </div>
      </Toolbar>

      <div className={classes.fields}>
        <div className={classes.fieldGroup}>
          <div className={classes.fieldSpacing}>
            <FormikWorkflowNameInput
              fullWidth
              label="Workflow Name"
              name="workflowName"
            />

            {values.workflowName && values.workflowName != "" && (
              <FormikVersionDropdown
                fullWidth
                label="Workflow version"
                name="workflowVersion"
                disabled={values.workflowName ? false : true}
                workflowName={values.workflowName ? values.workflowName : ""}
              />
            )}

            <FormikJsonInput
              reinitialize
              height={200}
              label="Input (JSON)"
              name="workflowInput"
              error={touched.workflowInput && !!errors.workflowInput}
              helperText={touched.workflowInput && errors.workflowInput}
              onChange={() => {}}
            />

            <FormikInput
              fullWidth
              label="Correlation ID"
              name="correlationId"
            />

            <FormikJsonInput
              className={classes.field}
              reinitialize
              height={200}
              label="Task to Domain (JSON)"
              name="taskToDomain"
              error={touched.taskToDomain && !!errors.taskToDomain}
              helperText={touched.taskToDomain && errors.taskToDomain}
              onChange={() => {}}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}

function runPayloadToFormData(runPayload) {
  return {
    workflowName: _.get(runPayload, "name", ""),
    workflowVersion: _.get(runPayload, "version", ""),
    workflowInput: _.has(runPayload, "input")
      ? JSON.stringify(runPayload.input, null, 2)
      : "",
    correlationId: _.get(runPayload, "correlationId", ""),
    taskToDomain: _.has(runPayload, "taskToDomain")
      ? JSON.stringify(runPayload.taskToDomain, null, 2)
      : "",
  };
}

function formDataToRunPayload(form) {
  let runPayload = {
    name: form.workflowName,
  };
  if (form.workflowVersion) {
    runPayload.version = form.workflowVersion;
  }
  if (form.workflowInput) {
    runPayload.input = JSON.parse(form.workflowInput);
  }
  if (form.correlationId) {
    runPayload.correlationId = form.correlationId;
  }
  if (form.taskToDomain) {
    runPayload.taskToDomain = JSON.parse(form.taskToDomain);
  }
  return runPayload;
}
